import { transformDocumentNumber } from '@/components/custom/erp';
import { MA_CHUNG_TU } from '@/constants';
import { FormFieldState } from '../hooks';

/**
 * Transform detail rows for API submission
 * @param detailRows - Array of detail row data from the form
 * @returns Transformed detail rows ready for API submission
 */
export const transformDetailRows = (detailRows: BanHangRecord[]) => {
  return detailRows.map((row: BanHangRecord, index: number) => ({
    line: index + 1,
    tk: row.tk_data?.uuid || '',
    ma_kh: row.ma_kh_data?.uuid || '',
    ps_no_nt: roundNumber(row.ps_no_nt || 0),
    ps_no: roundNumber(row.ps_no_nt || 0),
    ps_co_nt: roundNumber(row.ps_co_nt || 0),
    ps_co: roundNumber(row.ps_co_nt || 0),
    nh_dk: row.nhom || '',
    dien_giai: row.dien_giai || '',
    so_ct0: row.so_hd || '',
    ngay_ct0: row.ngay_hd || null,
    ma_bp: row.ma_bp_data?.uuid || '',
    ma_vv: row.ma_vv_data?.uuid || null,
    ma_hd: row.ma_hd_data?.uuid || null,
    ma_dtt: row.ma_dtt_data?.uuid || null,
    ma_ku: row.ma_ku_data?.uuid || null,
    ma_phi: row.ma_phi_data?.uuid || null,
    ma_sp: row.ma_sp_data?.uuid || null,
    ma_lsx: row.ma_lsx_data?.uuid || null,
    ma_cp0: row.ma_cp0_data?.uuid || null
  }));
};

export const transformTaxRows = (taxRows: BanHangRecord[]) => {
  return taxRows.map((row: BanHangRecord, index: number) => ({
    line: index + 1,
    so_ct0: row.so_hd || '',
    so_ct2: row.so_ct2 || '',
    ngay_ct0: row.ngay_hd || null,
    ma_thue: row.ma_thue || '',
    ma_mau_ct: row.ma_mau_ct || '',
    ma_mau_bc: row.ma_mau_bc || '',
    ma_tc_thue: row.ma_tc_thue || '',
    ma_kh: row.ma_ncc_data?.uuid || '',
    ten_kh_thue: row.ma_ncc_data?.customer_name || '',
    dia_chi: row.ma_ncc_data?.address || '',
    ma_so_thue: row.ma_ncc_data?.tax_code || '',
    ten_vt_thue: row.ten_vt_thue || '',
    t_tien_nt: row.t_tien_nt || 0,
    tk_thue_no: row.tk_thue_data?.uuid || '',
    tk_du: row.tk_doi_ung_data?.uuid || '',
    t_thue_nt: row.t_thue_nt || 0,
    ma_kh9: row.cuc_thue_data?.uuid || '',
    ma_tt: row.ma_tt_data?.uuid || '',
    ghi_chu: row.ghi_chu || '',
    ma_bp: row.ma_bp_data?.uuid || '',
    ma_vv: row.ma_vv_data?.uuid || null,
    ma_hd: row.ma_hd_data?.uuid || null,
    ma_dtt: row.ma_dtt_data?.uuid || null,
    ma_ku: row.ma_ku_data?.uuid || null,
    ma_phi: row.ma_phi_data?.uuid || null,
    ma_sp: row.ma_sp_data?.uuid || null,
    ma_lsx: row.ma_lsx_data?.uuid || null,
    ma_cp0: row.ma_cp0_data?.uuid || null
  }));
};

/**
 * Transform all form data for submission
 * @param data - Form data from the form submission
 * @param state - Form field state containing references to selected entities
 * @param detailRows - Array of detail row data
 * @param accountRows - Array of account row data
 * @returns Transformed data ready for API submission
 */
export const transformFormData = (
  data: BanHangPayload,
  state: FormFieldState,
  totalPSNo: number,
  totalPSCo: number,
  ...rest: BanHangRecord[]
) => {
  // Transform detail and account rows
  const detail = transformDetailRows(rest[0]);
  const tax = transformTaxRows(rest[1]);

  // Build the final form data object
  return {
    ...data,
    dien_giai: state.dienGiai || '',
    ty_gia: state.ty_gia,
    ngay_lct: data.ngay_ct || new Date().toISOString().split('T')[0],

    // Total fields for BottomActionBarV2
    t_ps_no_nt: roundNumber(totalPSNo),
    t_ps_no: roundNumber(totalPSNo),
    t_ps_co_nt: roundNumber(totalPSCo),
    t_ps_co: roundNumber(totalPSCo),

    // So chung tu
    ...transformDocumentNumber(state.quyenChungTu, state.soChungTu),

    // Detail arrays
    chi_tiet_items: detail,
    thue_items: tax
  };
};

const roundNumber = (number: number) => {
  return Math.round(number * 100) / 100;
};
